import { FC, forwardRef, MouseEventHandler, useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight, FileTextIcon, Star } from 'lucide-react';

import { env } from '@/constants/env';
import { ACCEPTED_MEDIA_TYPES } from '@/features/estate-media/schema/upload-media';
import { cn } from '@/lib/utils';
import { CarouselImage } from '@/pages/image-rendering';

interface ImageCarouselProps {
  preselectedCurrentIndex?: number;
  className?: string;
  imageWrapperClassName?: string | ((active: boolean) => string);
  imageClassName?: string;
  selectedImageId?: string;
  imageWidth?: number;
  images:
    | {
        createdAt: string | null;
        mediaId: string | null;
        updatedAt: string | null;
        fileType: string | null;
      }[]
    | CarouselImage[];
  showArrowButtons?: boolean;
  favouriteIds?: string[];
  onClickImage?: (imageId: CarouselImage) => void;
  onFavourite?: (imageId: string) => void;
}

export const ImageCarousel: FC<ImageCarouselProps> = forwardRef<HTMLDivElement, ImageCarouselProps>(
  (
    {
      images,
      preselectedCurrentIndex,
      className,
      imageWrapperClassName,
      imageClassName,
      selectedImageId,
      showArrowButtons = true,
      favouriteIds = [],
      onClickImage,
      onFavourite,
    },
    ref
  ) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const nextImage: MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      setCurrentIndex((prevIndex) => (prevIndex + 1) % (images?.length ?? 1));
    };

    const prevImage: MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      setCurrentIndex((prevIndex) => (prevIndex === 0 ? (images?.length ?? 1) - 1 : prevIndex - 1));
    };

    useEffect(() => {
      if (preselectedCurrentIndex) setCurrentIndex(preselectedCurrentIndex);
    }, [preselectedCurrentIndex]);

    return (
      <div ref={ref} className="relative mx-auto aspect-square size-full overflow-hidden">
        <div
          className={cn('flex size-full transition-transform duration-300 ease-in-out', className)}
          style={{
            width: `${images.length * 100}%`,
            transform: `translateX(-${(100 / images.length) * currentIndex}%)`,
          }}
        >
          {images?.map((image, index) => (
            <div
              key={`${image?.mediaId}-${index}`}
              className={cn(
                'relative flex h-full items-center justify-center',
                imageWrapperClassName
                  ? typeof imageWrapperClassName === 'string'
                    ? imageWrapperClassName
                    : imageWrapperClassName(selectedImageId === image?.mediaId)
                  : '',
                onClickImage ? 'cursor-pointer' : ''
              )}
              style={{ width: `${100 / (images?.length ?? 1)}%` }}
              onClick={() => {
                if (onClickImage) {
                  onClickImage(image as CarouselImage);
                }
              }}
            >
              {onFavourite ? (
                <div
                  className="absolute right-2 top-2 z-10 cursor-pointer"
                  onClick={() => {
                    if (image?.mediaId) {
                      onFavourite(image?.mediaId);
                    }
                  }}
                >
                  <Star
                    className={cn(
                      'text-primary-light',
                      image?.mediaId &&
                        favouriteIds.includes(image?.mediaId) &&
                        'fill-primary-light'
                    )}
                  />
                </div>
              ) : null}
              {image?.fileType !== ACCEPTED_MEDIA_TYPES.Pdf ? (
                <img
                  src={`${env.API_URL}/images/${image?.mediaId}`}
                  alt="carousel"
                  className={cn('max-h-[60vh] max-w-full object-contain', imageClassName)}
                />
              ) : (
                <div className="flex size-full items-center justify-center">
                  <FileTextIcon viewBox="0 0 25 25" width={40} height={40} />
                </div>
              )}
            </div>
          ))}
        </div>
        {showArrowButtons ? (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/50 p-2 text-white"
            >
              <ChevronLeft className="[&_*]:stroke-primary" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/50 p-2 text-white"
            >
              <ChevronRight className="[&_*]:stroke-primary" />
            </button>
          </>
        ) : null}
      </div>
    );
  }
);
